import { type ReactNode } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

/**
 * Wraps any /auth/* page. If the user is already signed in, bounce them
 * back to where they were heading (`?next=`) or the dashboard. Prevents
 * the confusing state where a signed-in user sees the login form and
 * gets a stale error because Cognito rejects a same-session sign-in.
 *
 * Pair: <RequireAuth> for the inverse direction.
 */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [params] = useSearchParams();

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center text-text-muted text-sm">
        Loading…
      </div>
    );
  }

  if (status === "authenticated") {
    // An already-signed-in parent opening an invite link (e.g. linking a
    // second child) skips the signup form entirely and goes straight to
    // the manual redemption page, code pre-filled.
    const invite = params.get("invite");
    if (invite) {
      return <Navigate to={`/parent/link?code=${encodeURIComponent(invite)}`} replace />;
    }
    const raw = params.get("next") || "/dashboard";
    // Guard against open-redirect: only allow same-origin relative paths.
    const next = raw.startsWith("/") && !raw.startsWith("//") ? raw : "/dashboard";
    return <Navigate to={next} replace />;
  }

  return <>{children}</>;
}
